import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ArrowUpRight, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 2000;
const MAX_POLLS = 20; // ~40 seconds

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [order, setOrder] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          clear();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // transient errors — keep polling until we hit max attempts
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <main className="min-h-screen bg-charcoal flex items-center justify-center px-6 py-24">
      <div className="max-w-2xl w-full text-center">
        {state === "polling" && (
          <>
            <Loader2 size={48} className="text-rose animate-spin mx-auto mb-6" />
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// CONFIRMING PAYMENT</p>
            <h1 className="font-serif-display italic text-4xl md:text-5xl text-off-white">One moment…</h1>
            <p className="mt-4 text-off-white/70 font-light">We're finalizing your order with Stripe.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-rose mx-auto mb-6" strokeWidth={1.5} />
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// PAYMENT CONFIRMED</p>
            <h1 className="font-serif-display italic text-5xl md:text-7xl text-off-white leading-[0.95]">
              Whipped up<br />&amp; on the way.
            </h1>
            <p className="mt-6 text-off-white/70 font-light max-w-lg mx-auto">
              Thank you — your order is confirmed. A receipt has been sent from Stripe.
              We hand-whip and ship within 48 hours.
            </p>

            {order && order.items?.length > 0 && (
              <div className="mt-10 border border-off-white/15 p-6 text-left max-w-lg mx-auto">
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// ORDER</p>
                <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 mb-4">
                  SESSION: {order.session_id.slice(0, 22)}…
                </p>
                <ul className="space-y-2">
                  {order.items.map((it, i) => (
                    <li key={i} className="flex justify-between text-sm border-b border-off-white/10 pb-2">
                      <span className="text-off-white">{it.qty} × {it.name}</span>
                      <span className="font-mono text-off-white/70">${it.line_total.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-3 flex justify-between border-t border-off-white/15">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-off-white/60">TOTAL</span>
                  <span className="font-display text-xl text-off-white">${order.amount.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link
                to="/shop"
                className="btn-blueprint inline-flex items-center gap-2 h-12 px-7 bg-rose text-charcoal font-mono text-[11px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only"
              >
                CONTINUE SHOPPING <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center h-12 px-7 border border-off-white/30 text-off-white font-mono text-[11px] tracking-[0.25em] hover:border-rose hover:text-rose transition-colors-only"
              >
                HOME
              </Link>
            </div>
          </>
        )}

        {state === "timeout" && (
          <>
            <Loader2 size={40} className="text-off-white/50 mx-auto mb-6" strokeWidth={1.5} />
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// STILL PROCESSING</p>
            <h1 className="font-serif-display italic text-3xl md:text-4xl text-off-white">This is taking a moment.</h1>
            <p className="mt-4 text-off-white/70 font-light">Your payment may still be finalizing. Refresh in a minute, or check your email for a Stripe receipt.</p>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle size={48} className="text-rose-deep mx-auto mb-6" strokeWidth={1.5} />
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose-deep mb-3">// PAYMENT ISSUE</p>
            <h1 className="font-serif-display italic text-4xl md:text-5xl text-off-white">Something didn't go through.</h1>
            <p className="mt-4 text-off-white/70 font-light">Please try again — you weren't charged.</p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-2 h-12 px-7 border border-off-white/30 hover:border-rose hover:text-rose font-mono text-[11px] tracking-[0.25em] transition-colors-only"
            >
              BACK TO SHOP
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
